import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Panel, PanelGroup, PanelResizeHandle } from '@/components/ui/resizable';
import useGetPlatformCostsOverview from '@/hooks/admin/platformCosts/useGetPlatformCostsOverview';
import useListCustomCostProviders from '@/hooks/admin/platformCosts/useListCustomCostProviders';
import AddCustomProviderSheet from './AddCustomProviderSheet';
import PeriodPicker from './PeriodPicker';
import buildSidebarItems from './helpers/buildSidebarItems';
import useSelectedCostMonth from './helpers/useSelectedCostMonth';
import PlatformCostsSidebar from './PlatformCostsSidebar';
import {
    Body,
    Content,
    PlatformCostsContainer,
    SidebarContainer,
    TopBar,
    TopBarLeft,
    TopBarSubtitle,
    TopBarTitle,
} from './styled';

function PlatformCosts() {
    const navigate = useNavigate();
    const { month, period, setMonth } = useSelectedCostMonth();
    const { overview } = useGetPlatformCostsOverview({ period });
    const { providers: customProviders } = useListCustomCostProviders({ month, enabled: true });
    const [isAddCustomOpen, setIsAddCustomOpen] = useState(false);

    const providerSpendById = Object.fromEntries(
        (overview?.providers ?? []).map(provider => [provider.id, provider.monthToDateCents])
    );
    const sidebarItems = buildSidebarItems({
        providerSpendById,
        overviewMonthToDateCents: overview?.monthToDateCents ?? null,
        customProviders: customProviders.map(provider => ({
            id: provider.id,
            displayName: provider.displayName,
        })),
    });

    return (
        <PlatformCostsContainer>
            <TopBar>
                <TopBarLeft>
                    <TopBarTitle>Platform Costs</TopBarTitle>
                    <TopBarSubtitle>
                        Infrastructure and vendor spend across the platform
                    </TopBarSubtitle>
                </TopBarLeft>
                <PeriodPicker month={month} onMonthChange={setMonth} />
            </TopBar>
            <Body>
                <PanelGroup direction="horizontal" className="flex-1 min-h-0">
                    <Panel defaultSize={18} minSize={14} maxSize={32}>
                        <SidebarContainer>
                            <PlatformCostsSidebar
                                items={sidebarItems}
                                month={month}
                                currency={overview?.currency ?? 'usd'}
                                onAddCustomProvider={() => setIsAddCustomOpen(true)}
                            />
                        </SidebarContainer>
                    </Panel>
                    <PanelResizeHandle />
                    <Panel defaultSize={82} minSize={50}>
                        <Content>
                            <Outlet />
                        </Content>
                    </Panel>
                </PanelGroup>
            </Body>
            <AddCustomProviderSheet
                isOpen={isAddCustomOpen}
                month={month}
                onRequestClose={() => setIsAddCustomOpen(false)}
                onCreated={({ providerId, month: createdMonth }) => {
                    navigate(`/admin/platform-costs/${providerId}?month=${createdMonth}`);
                }}
            />
        </PlatformCostsContainer>
    );
}

export default PlatformCosts;
